import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import './Donations.css';

function Donations() {
  const [donations, setDonations] = useState([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [platform, setPlatform] = useState('all');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total: 0,
    totalAmount: 0,
    avgAmount: 0,
    topDonor: null
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDonation, setNewDonation] = useState({
    platform: 'twitch-main',
    donor_name: '',
    amount: '',
    currency: 'EUR',
    message: ''
  });
  const alert = useAlert();

  useEffect(() => {
    fetchDonations();
  }, [timeRange, platform]);

  const fetchDonations = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/donations', {
        params: { timeRange, platform }
      });
      setDonations(response.data.donations);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching donations:', error);
      alert.error('Fehler beim Laden der Donations');
    } finally {
      setLoading(false);
    }
  };

  const addDonation = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/donations', newDonation);
      alert.success('Donation hinzugefügt!');
      setShowAddModal(false);
      setNewDonation({
        platform: 'twitch-main',
        donor_name: '',
        amount: '',
        currency: 'EUR',
        message: ''
      });
      fetchDonations();
    } catch (error) {
      console.error('Error adding donation:', error);
      alert.error('Fehler beim Hinzufügen');
    }
  };

  const deleteDonation = async (id) => {
    if (!window.confirm('Donation wirklich löschen?')) return;
    
    try {
      await axios.delete(`/api/donations/${id}`);
      alert.success('Donation gelöscht');
      fetchDonations();
    } catch (error) {
      console.error('Error deleting donation:', error);
      alert.error('Fehler beim Löschen');
    }
  };

  const exportDonations = async () => {
    try {
      const response = await axios.get('/api/donations/export', {
        params: { timeRange, platform },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `donations-${timeRange}-${platform}-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert.success('Donations exportiert!');
    } catch (error) {
      console.error('Error exporting donations:', error);
      alert.error('Fehler beim Exportieren');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <div className="donations">
      <div className="page-header">
        <h2>💰 Donations</h2>
        <p>Verwalte und analysiere deine Donations</p>
      </div>

      <div className="donations-controls">
        <div className="filter-group">
          <label>Zeitraum:</label>
          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="24h">Letzte 24 Stunden</option>
            <option value="7d">Letzte 7 Tage</option>
            <option value="30d">Letzte 30 Tage</option>
            <option value="90d">Letzte 90 Tage</option>
            <option value="all">Alle Zeit</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Plattform:</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="all">Alle Plattformen</option>
            <option value="twitch-main">Twitch Main</option>
            <option value="youtube">YouTube</option>
            <option value="streamelements">StreamElements</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>

        <button className="btn-success" onClick={() => setShowAddModal(true)}>
          ➕ Donation hinzufügen
        </button>

        <button className="btn-primary" onClick={exportDonations}>
          📥 Export CSV
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Lade Donations...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="summary-grid">
            <div className="summary-card total">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <h3>Gesamt Betrag</h3>
                <p className="card-value">{formatCurrency(summary.totalAmount)}</p>
              </div>
            </div>

            <div className="summary-card count">
              <div className="card-icon">🎁</div>
              <div className="card-content">
                <h3>Anzahl Donations</h3>
                <p className="card-value">{summary.total}</p>
              </div>
            </div>

            <div className="summary-card average">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <h3>Durchschnitt</h3>
                <p className="card-value">{formatCurrency(summary.avgAmount)}</p>
              </div>
            </div>

            <div className="summary-card top-donor">
              <div className="card-icon">👑</div>
              <div className="card-content">
                <h3>Top Donor</h3>
                <p className="card-value">{summary.topDonor?.donor_name || 'N/A'}</p>
                {summary.topDonor && (
                  <p className="card-subvalue">{formatCurrency(summary.topDonor.total)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Donations Table */}
          <div className="donations-table-container">
            <h3>💸 Donation History</h3>
            {donations.length === 0 ? (
              <div className="empty-state">
                <p>Keine Donations im ausgewählten Zeitraum</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="donations-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Donor</th>
                      <th>Plattform</th>
                      <th>Betrag</th>
                      <th>Nachricht</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((donation) => (
                      <tr key={donation.id}>
                        <td>{formatDate(donation.timestamp)}</td>
                        <td className="donor-name">{donation.donor_name}</td>
                        <td>
                          <span className={`platform-badge ${donation.platform}`}>
                            {donation.platform}
                          </span>
                        </td>
                        <td className="amount">
                          {formatCurrency(donation.amount, donation.currency)}
                        </td>
                        <td className="message">
                          {donation.message || <span className="no-message">-</span>}
                        </td>
                        <td>
                          <button
                            className="btn-danger btn-small"
                            onClick={() => deleteDonation(donation.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Donation Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Donation hinzufügen</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={addDonation}>
              <div className="form-group">
                <label>Plattform:</label>
                <select
                  value={newDonation.platform}
                  onChange={(e) => setNewDonation({ ...newDonation, platform: e.target.value })}
                  required
                >
                  <option value="twitch-main">Twitch Main</option>
                  <option value="youtube">YouTube</option>
                  <option value="streamelements">StreamElements</option>
                  <option value="paypal">PayPal</option>
                </select>
              </div>

              <div className="form-group">
                <label>Donor Name:</label>
                <input
                  type="text"
                  value={newDonation.donor_name}
                  onChange={(e) => setNewDonation({ ...newDonation, donor_name: e.target.value })}
                  required
                  placeholder="Name des Donors"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Betrag:</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newDonation.amount}
                    onChange={(e) => setNewDonation({ ...newDonation, amount: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Währung:</label>
                  <select
                    value={newDonation.currency}
                    onChange={(e) => setNewDonation({ ...newDonation, currency: e.target.value })}
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Nachricht (optional):</label>
                <textarea
                  value={newDonation.message}
                  onChange={(e) => setNewDonation({ ...newDonation, message: e.target.value })}
                  placeholder="Donation Nachricht..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-success">
                  Hinzufügen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Donations;
